import React, { memo } from 'react';
import { createPortal } from 'react-dom';
import { classNames } from '../../../utils/classNames';
import type {
  MentionPopupProps,
  MentionPopupDisplayMode
} from './MentionPopup.types';
import type { User } from '../../../utils/userUtils';
import './MentionPopup.css';

// Helper function to render user based on display mode
const renderUserDisplay = (
  user: User,
  displayMode: MentionPopupDisplayMode
) => {
  // If custom function provided, use it
  if (typeof displayMode === 'function') {
    return displayMode(user);
  }

  // Built-in display modes
  switch (displayMode) {
    case 'name':
      return <div className="mention-popup__name-only">{user.name}</div>;

    case 'username':
      return <div className="mention-popup__name-only">@{user.username}</div>;

    case 'email':
      return <div className="mention-popup__name-only">{user.email}</div>;

    case 'both':
      return (
        <div className="mention-popup__user-info">
          <div className="mention-popup__name">{user.name}</div>
          <div className="mention-popup__email">{user.email}</div>
        </div>
      );

    default:
      return <div className="mention-popup__name-only">{user.name}</div>;
  }
};

const MentionPopupComponent: React.FC<MentionPopupProps> = ({
  users,
  loading,
  selectedIndex,
  position,
  onSelect,
  style,
  placement = 'below',
  displayMode = 'name'
}) => {
  if (!users.length && !loading) return null;

  return createPortal(
    <div
      className={classNames('mention-popup', `mention-popup--${placement}`)}
      style={{
        left: position.x,
        top: position.y,
        maxHeight: position.maxHeight || 300,
        ...style
      }}
      onMouseDown={e => e.preventDefault()} // Prevent focus loss when clicking in popup
    >
      {loading && (
        <div className="mention-popup__loading">
          <div className="mention-popup__spinner"></div>
          Searching...
        </div>
      )}

      {users.map((user, index) => (
        <div
          key={user.id}
          className={classNames(
            'mention-popup__item',
            index === selectedIndex && 'mention-popup__item--selected'
          )}
          onMouseDown={e => {
            e.preventDefault(); // Prevent blur from firing
            onSelect(user);
          }}
          onClick={() => onSelect(user)} // Fallback for accessibility
        >
          {renderUserDisplay(user, displayMode)}
        </div>
      ))}
    </div>,
    document.body
  );
};

export const MentionPopup = memo(MentionPopupComponent);
